/**
 * AuthenticatedLayout - Layout para rutas autenticadas
 * 
 * Incluye la cabecera de navegación y envuelve el contenido de las páginas
 */

import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { PlaylistsProvider } from '../contexts/PlaylistsContext';
import AppHeader from '../components/AppHeader';
import { AdminNavButtons } from '../components/AdminNavButtons';
import DJAssistant from '../components/DJAssistant';
import DossierGenerator from '../components/DossierGenerator';
import UpdateNotification from '../components/AutoUpdate';
import { Toaster } from 'sonner';
import { Dialog, DialogContent } from '../components/ui/dialog';
import PageLoader from '../components/PageLoader';

// Mapeo path → identificador de vista usado por AdminNavButtons para marcar botón activo
const PATH_TO_VIEW = {
  '/team': 'team',
  '/staff-timing': 'staff-timing',
  '/users': 'users',
  '/precios': 'precios',
  '/finances': 'finanzas',
  '/presupuestos': 'presupuestos',
  '/plantillas': 'plantillas',
  '/mp3-library': 'mp3-library',
  '/venues': 'venues',
  '/settings': 'settings',
};

// Componente interno que usa los contextos
const LayoutContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, user, token } = useAuth();
  const [isDossierOpen, setIsDossierOpen] = useState(false);
  const view = PATH_TO_VIEW[location.pathname] || 'dashboard';
  
  // Redirect si no autenticado — pero SOLO cuando ya terminó de verificar la sesión.
  // Fix feb-2026: sin `loading===false`, el redirect podía disparar mientras
  // `useAuth /api/auth/me` estaba aún en curso → deep-links a /reports, /calendar,
  // /trends, etc. rebotaban a "/" antes de resolver la sesión.
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/');
    }
  }, [loading, isAuthenticated, navigate]);

  // Función para ir a MainApp con una vista específica (botones que apuntan a vistas internas)
  const handleSetView = useCallback((newView) => {
    navigate(`/?view=${newView}`);
  }, [navigate]);

  // Función para abrir el generador de dossier
  const handleOpenDossier = useCallback(() => {
    setIsDossierOpen(true);
  }, []);

  // Fetch precios (placeholder - se conectará cuando se necesite)
  const fetchPrecios = useCallback(async () => {
    // Implementar si es necesario
  }, []);

  // Placeholders para AdminNavButtons — la carga real se hace al llegar a MainApp
  const noop = useCallback(() => {}, []);

  // Mientras se resuelve la sesión, mostrar un loader (evita renderizar layout vacío
  // ni disparar redirect prematuro a "/").
  if (loading) {
    return <PageLoader fullScreen />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 safe-top safe-x">
      <UpdateNotification />
      <Toaster position="top-center" richColors />
      
      <div className="container mx-auto px-2 sm:px-3 py-2 sm:py-4 max-w-7xl">
        <AppHeader 
          view={view}
          setView={handleSetView}
          isLiveMode={false}
          fetchPrecios={fetchPrecios}
          onOpenDossier={handleOpenDossier}
          adminNavSlot={
            user?.role === 'admin' ? (
              <AdminNavButtons
                view={view}
                setView={handleSetView}
                navigate={navigate}
                fetchPrecios={fetchPrecios}
                setIsDossierOpen={setIsDossierOpen}
                fetchTemplates={noop}
                fetchSmartPlaylistTemplates={noop}
              />
            ) : null
          }
        />

        {/* Contenido de la página envuelto en Suspense para que las páginas lazy
            no colapsen todo el layout: el header + tabs permanecen visibles y solo
            el área de contenido muestra el loader durante la hidratación. */}
        <div className="mt-4">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
      
      {/* DJ Assistant - Botón flotante disponible en todas las páginas */}
      <DJAssistant eventContext={null} />
      
      {/* Dossier Generator Modal - disponible en todas las páginas */}
      <Dialog open={isDossierOpen} onOpenChange={setIsDossierOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DossierGenerator onClose={() => setIsDossierOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Layout wrapper que provee los contextos necesarios
const AuthenticatedLayout = () => {
  return (
    <PlaylistsProvider>
      <LayoutContent />
    </PlaylistsProvider>
  );
};

export default AuthenticatedLayout;
